import {
  ConsumeOperation,
  isConsumeOperation,
  ProcessorCallbacks,
  QueueOperation,
  UndoOperation,
} from "../types/queue.ts";
import { consumeProductStock, undoStockTransaction } from "utils/grocyApi.ts";

export class QueueProcessor {
  private isProcessing = false;
  private queue: QueueOperation[] = [];
  private callbacks: ProcessorCallbacks;

  constructor(callbacks: ProcessorCallbacks) {
    this.callbacks = callbacks;
  }

  onQueueChange(newQueue: QueueOperation[]) {
    this.queue = newQueue;
    this.startProcessing();
  }

  private async startProcessing() {
    if (this.isProcessing) return;

    this.isProcessing = true;

    try {
      await this.processAllPending();
    } finally {
      this.isProcessing = false;
    }
  }

  private async processAllPending() {
    console.log("Processing queue", this.queue);

    // Find the first pending operation in queue order
    const nextOp = this.queue.find((op) => op.status === "PENDING");

    if (!nextOp) {
      // Check if there are any active operations (pending or in progress)
      const hasActiveOperations = this.queue.some((op) =>
        op.status === "PENDING" || op.status === "IN_PROGRESS"
      );

      if (!hasActiveOperations) {
        // Queue is truly empty of all active work - notify hook to refresh
        this.callbacks.onQueueEmpty();
      }
      return;
    }

    // Check if this operation can be processed
    if (!this.canProcessOperation(nextOp)) {
      // Can't process the next operation due to dependencies
      // Stop processing to maintain queue order
      console.log("Waiting for dependencies for operation", nextOp.id);
      return;
    }

    await this.processSingleOperation(nextOp);
  }

  private canProcessOperation(operation: QueueOperation): boolean {
    if (operation.type === "CONSUME") {
      return true; // CONSUME operations can always be processed
    }

    if (operation.type === "UNDO") {
      // UNDO operations can only be processed if their target CONSUME is completed
      const targetOp = this.queue.find((op) =>
        op.id === operation.targetOperationId
      );
      return targetOp?.status === "COMPLETED" && isConsumeOperation(targetOp) &&
        !!targetOp.transactionId;
    }

    return false;
  }

  private async processSingleOperation(operation: QueueOperation) {
    this.callbacks.updateOperationStatus(operation.id, "IN_PROGRESS");

    try {
      switch (operation.type) {
        case "CONSUME":
          await this.processConsume(operation);
          break;
        case "UNDO":
          await this.processUndo(operation);
          break;
        default:
          const _exhaustive: never = operation;
          throw new Error(`Unknown operation type: ${_exhaustive}`);
      }

      this.callbacks.updateOperationStatus(operation.id, "COMPLETED");

      // Notify about operation completion
      this.callbacks.onOperationCompleted(operation);

      // Check for completed pairs and clean them up
      this.cleanupCompletedPairs(operation);

      // Don't rely on synchronous React state propagation here.
      // Re-check the queue on the next tick after the hook applies status updates.
      setTimeout(() => this.startProcessing(), 0);
    } catch (error) {
      console.error("Error processing operation", operation, error);
      const errorMessage = error instanceof Error
        ? error.message
        : String(error);
      this.callbacks.updateOperation(operation.id, {
        status: "FAILED",
        errorMessage,
      });
      this.callbacks.onOperationFailed(operation);

      // If a CONSUME operation fails, fail any UNDO operations targeting it
      if (operation.type === "CONSUME") {
        this.failDependentUndoOperations(operation.id);
      }

      // Keep processing later queue entries even when one operation fails.
      setTimeout(() => this.startProcessing(), 0);
    }
  }

  private async processConsume(operation: ConsumeOperation) {
    const transactionId = await consumeProductStock({
      productId: operation.productId,
      amount: operation.amount ?? 1,
    });

    this.callbacks.updateOperation(operation.id, { transactionId });
  }

  private async processUndo(operation: UndoOperation) {
    const targetOp = this.queue.find((op) =>
      op.id === operation.targetOperationId
    );

    if (!targetOp) {
      throw new Error(
        `Target operation ${operation.targetOperationId} not found`,
      );
    }

    if (!isConsumeOperation(targetOp)) {
      throw new Error(
        `Target operation ${operation.targetOperationId} is not a CONSUME operation`,
      );
    }

    if (targetOp.status === "FAILED") {
      throw new Error(
        `Cannot undo failed operation ${operation.targetOperationId}`,
      );
    }

    if (targetOp.status !== "COMPLETED" || !targetOp.transactionId) {
      throw new Error(
        `Target operation ${operation.targetOperationId} is not completed or missing transaction ID`,
      );
    }

    await undoStockTransaction(targetOp.transactionId);
  }

  private cleanupCompletedPairs(completedOperation: QueueOperation) {
    if (completedOperation.type === "CONSUME") {
      // Look for completed UNDO operations targeting this CONSUME
      const matchingUndo = this.queue.find((op) =>
        op.type === "UNDO" &&
        op.status === "COMPLETED" &&
        op.targetOperationId === completedOperation.id
      );

      if (matchingUndo) {
        this.callbacks.removeCompletedPair(
          completedOperation.id,
          matchingUndo.id,
        );
      }
    } else if (completedOperation.type === "UNDO") {
      // Check if the target CONSUME operation is also completed
      const targetConsume = this.queue.find((op) =>
        op.id === completedOperation.targetOperationId &&
        op.type === "CONSUME" &&
        op.status === "COMPLETED"
      );

      if (targetConsume) {
        this.callbacks.removeCompletedPair(
          targetConsume.id,
          completedOperation.id,
        );
      }
    }
  }

  private failDependentUndoOperations(failedConsumeId: string) {
    const dependentUndos = this.queue.filter((op) =>
      op.type === "UNDO" &&
      op.targetOperationId === failedConsumeId &&
      op.status === "PENDING"
    );

    dependentUndos.forEach((undoOp) => {
      this.callbacks.updateOperationStatus(undoOp.id, "FAILED");
      this.callbacks.onOperationFailed(undoOp);
    });
  }
}
